import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Check, ChevronsUpDown, TriangleAlert } from "lucide-react"

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import { csrfToken } from "@/lib/orderActions"

// A project combobox whose list stays physically inside DialogContent. Keeping
// it inline avoids a second portal/focus scope, which is important for long,
// keyboard-driven lists inside a modal.
function DialogProjectCombobox({ options, value, onValueChange }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const triggerRef = useRef(null)
  const searchRef = useRef(null)
  const selected = options.find((option) => option.value === value)
  const visibleOptions = query
    ? options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
    : options

  useEffect(() => {
    if (!open) return undefined

    setQuery("")
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [open])

  function closeAndFocus() {
    setOpen(false)
    window.requestAnimationFrame(() => triggerRef.current?.focus())
  }

  return (
    <div className="relative">
      <Button
        ref={triggerRef}
        id="access-project"
        type="button"
        variant="outline"
        role="combobox"
        aria-label="Project"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="w-full justify-between font-normal"
      >
        <span className={cn("truncate", !selected && "text-muted-foreground")}>
          {selected?.label ?? "Choose a project"}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute top-full right-0 left-0 z-20 mt-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
          <Command
            shouldFilter={false}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return
              event.preventDefault()
              event.stopPropagation()
              closeAndFocus()
            }}
          >
            <CommandInput
              ref={searchRef}
              value={query}
              onValueChange={setQuery}
              placeholder="Search projects..."
            />
            <CommandList>
              <CommandEmpty>No projects found.</CommandEmpty>
              <CommandGroup>
                {visibleOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onValueChange(option.value)
                      closeAndFocus()
                    }}
                  >
                    <Check className={cn("size-4", option.value === value ? "opacity-100" : "opacity-0")} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}

// The invite / add-a-project modal. Structure copied from profile/EditProfileModal
// (Inertia router POST + the Rails CSRF header; the redirect re-renders the page).
// Two modes off `lockedEmail`:
//   · null      → invite a NEW email to a chosen project.
//   · an email  → add-a-project for an EXISTING person (email locked).
// At-cap disables only the NEW-email path (`blocked`); add-a-project for an
// existing person stays enabled — the server's has_full_management_team? is false
// for an already-known email (§6). The server is the source of truth regardless.
export default function InviteModal({ open, onOpenChange, orders = [], lockedEmail = null, atCap = false }) {
  const [orderId, setOrderId] = useState("")
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const projectOptions = useMemo(
    () => orders.map((order) => ({ value: String(order.id), label: order.title })),
    [orders]
  )

  // Re-seed on open so stale state never lingers between openings / modes.
  useEffect(() => {
    if (!open) return
    setOrderId(orders[0] ? String(orders[0].id) : "")
    setEmail(lockedEmail ?? "")
  }, [open, lockedEmail, orders])

  const blocked = atCap && !lockedEmail

  function submit(event) {
    event.preventDefault()
    if (blocked) return

    setSubmitting(true)
    router.post(
      "/access",
      { orderId, email },
      {
        headers: { "X-CSRF-Token": csrfToken() },
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-[520px]">
        <DialogHeader className="px-[22px] pt-5 pb-3.5 text-left">
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">
            {lockedEmail ? "Add a project" : "Invite to a project"}
          </DialogTitle>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            They&apos;ll get an email to view the manuscript. New to Dibbly? They&apos;ll be prompted to make a
            free account first.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4 px-[22px] pt-1 pb-4">
          {blocked && (
            <div className="flex items-start gap-2 rounded-[10px] border border-[color-mix(in_oklab,var(--primary)_30%,transparent)] bg-[var(--red-soft)] px-3.5 py-3 text-[12.5px] text-primary">
              <TriangleAlert className="size-4 shrink-0" strokeWidth={2} />
              <span>
                <b className="font-semibold">You&apos;ve reached 3 collaborators.</b> Remove someone before
                adding another person.
              </span>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="access-project">Project</Label>
            <DialogProjectCombobox
              options={projectOptions}
              value={orderId}
              onValueChange={setOrderId}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="access-email">Invite by email</Label>
            <Input
              id="access-email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={Boolean(lockedEmail)}
              aria-label={lockedEmail ? "Invite by email (locked)" : "Invite by email"}
            />
          </div>

          <div className="rounded-[10px] border border-border bg-secondary px-3.5 py-3 text-[12px] leading-[1.5] text-ink-3">
            <b className="font-semibold text-foreground">Heads up —</b> you can&apos;t invite the project&apos;s
            writer or editor (they&apos;re already on the team), or your own email. Access is read &amp; comment
            on the document only.
          </div>

          <DialogFooter className="mt-1 gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || blocked || !orderId || !email}>
              {submitting ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
